/**
 * What has been done to a cell.
 *
 * The first seven are one sequence: the mayor's own development, a click at a
 * time, `NATURAL` through to a painted parking space. `SEALED` is not on that
 * sequence and cannot be reached from it — see below.
 */
export enum CellState {
  NATURAL = 'natural',
  SURVEYED = 'surveyed',
  CLEARED = 'cleared',
  GRADED = 'graded',
  POURED = 'poured',
  CURED = 'cured',
  PAINTED = 'painted',
  /**
   * Under a federal slab: a facility's footprint (ADR-015).
   *
   * Deliberately *not* one of the paved states, though it is concrete and it is
   * permanent. The mayor's pavement is a parking lot with cars on it and street
   * lighting over it; a facility's foundation is a machine's, and nothing drives
   * across it. Reusing `CURED` would have been one fewer enum member and would
   * have routed traffic through the data centre — a 6×4 slab is three cells wide
   * in both directions, which is exactly what `isHighwayCell` looks for.
   *
   * What it shares with pavement is everything that matters to the ground under
   * it: it is not `NATURAL`, so no bush on it bears fruit, no house is sited on
   * it, and no ant forages there. Land does not come back (design §19).
   */
  SEALED = 'sealed',
}

export interface Cell {
  x: number;
  y: number;
  state: CellState;
  // Meaningless but displayed stats
  soilDensity: number;      // 0-100
  drainage: number;         // 0-100
  developmentPotential: number; // Always 94-99%
  // Visual variation
  vegetationType: 'grass' | 'bush' | 'tree' | 'none';
  heightVariation: number;  // -1 to 1, for grading
  // Construction progress
  concreteLevel: number;    // 0-1 for pour animation
  cureProgress: number;     // 0-1 for cure animation
}

/** A cell that has been touched, as it appears in a snapshot. */
export interface TerrainCellSnapshot {
  x: number;
  y: number;
  state: CellState;
  concreteLevel: number;
  cureProgress: number;
}

/** The grid's complete state. See `TerrainGrid.save()`. */
export interface TerrainSnapshot {
  cells: TerrainCellSnapshot[];
  /**
   * Cells somebody has planted a bush on. See `TerrainGrid.plantBush`.
   *
   * Separate from `cells` because a planted cell is `NATURAL`, and `cells` only
   * carries the ones that are not. Empty in every district that has never had a
   * hedgerow, which is every district recorded before ADR-020.
   */
  planted: Array<{ x: number; y: number }>;
  terrainVersion: number;
}

export interface GridCoord {
  x: number;
  y: number;
}

/**
 * Positional noise for terrain generation, in [0, 1).
 *
 * The map is a pure function of position and a salt, so it needs no generator
 * state and is identical however the world is entered. Integer-only arithmetic
 * (`Math.imul` and shifts) is deliberate: the previous implementation was
 * `Math.sin(seed) * 10000`, and the low bits of a sine are a property of the
 * engine's libm rather than of the language. A browser and a server on
 * different V8 builds could have grown different meadows — and since bushes
 * become food sources, different meadows are different colonies. See
 * `docs/determinism.md`.
 *
 * Terrain is not yet seeded per district; every world still gets the same map.
 * Giving each district its own is Phase 2's business.
 */
export function cellNoise(x: number, y: number, salt: number): number {
  let h = Math.imul(x + 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (y + 0xc2b2ae35), 0x27d4eb2f);
  h = Math.imul(h ^ Math.imul(salt, 0x165667b1), 0x9e3779b1);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

/**
 * The ground: 40 × 30 cells and what has been done to each of them.
 *
 * Grid coordinates only. Until ADR-009 this class also held the camera — a pan
 * offset, a zoom, and the isometric projection to pixels — which is now
 * `rendering/Camera.ts`, on the client's side of the line. Nothing in the
 * simulation should be able to ask where something is *on screen*, because the
 * answer depends on a window that a settlement worker does not have.
 */
export class TerrainGrid {
  private cells: Cell[][] = [];
  private readonly cols: number;
  private readonly rows: number;

  /**
   * Cells whose bush somebody planted, by `"x,y"`.
   *
   * A cell's vegetation is otherwise a pure function of its position
   * (`cellNoise`), which is why it needs recording at all: `initializeCells()`
   * rebuilds the map from arithmetic, and a hedgerow is the one thing on natural
   * ground that arithmetic does not know about.
   *
   * Never shrinks. A planted cell that is later paved or sealed keeps its entry
   * and stops counting as a bush, because `countStandingVegetation` asks about
   * `NATURAL` ground and pavement does not come up (design §19).
   */
  private readonly planted = new Set<string>();

  // Terrain version counter - incremented on terrain changes for cache invalidation
  private _terrainVersion = 0;

  constructor(cols = 40, rows = 30) {
    this.cols = cols;
    this.rows = rows;
    this.initializeCells();
  }

  private initializeCells(): void {
    for (let y = 0; y < this.rows; y++) {
      this.cells[y] = [];
      for (let x = 0; x < this.cols; x++) {
        this.cells[y][x] = this.createNaturalCell(x, y);
      }
    }
  }

  private createNaturalCell(x: number, y: number): Cell {
    // Use different salts for different properties (more variety)
    const rand1 = cellNoise(x, y, 1);
    const rand2 = cellNoise(x, y, 2);
    const rand3 = cellNoise(x, y, 3);
    const rand4 = cellNoise(x, y, 4);

    // Determine vegetation - more trees and bushes
    let vegetationType: Cell['vegetationType'] = 'grass';
    if (rand1 > 0.88) {
      vegetationType = 'tree';
    } else if (rand1 > 0.65) {
      vegetationType = 'bush';
    } else if (rand1 < 0.1) {
      vegetationType = 'none'; // Some bare patches
    }

    // Soil density varies more (rocky areas, soft areas)
    let soilDensity: number;
    if (rand2 < 0.15) {
      soilDensity = 20 + Math.floor(rand2 * 100); // Rocky/poor
    } else if (rand2 > 0.85) {
      soilDensity = 85 + Math.floor(rand2 * 15); // Excellent
    } else {
      soilDensity = 45 + Math.floor(rand2 * 40); // Average
    }

    // Drainage varies (wet areas, dry areas)
    let drainage: number;
    if (rand3 < 0.1) {
      drainage = 15 + Math.floor(rand3 * 200); // Poor drainage (wet)
    } else if (rand3 > 0.9) {
      drainage = 90 + Math.floor(rand3 * 10); // Excellent
    } else {
      drainage = 40 + Math.floor(rand3 * 45);
    }

    // Development potential - still always "good" but varies more visibly
    // (The joke is it's always acceptable no matter what)
    const devPotential = 70 + Math.floor(rand4 * 29); // 70-99%

    return {
      x,
      y,
      state: CellState.NATURAL,
      soilDensity,
      drainage,
      developmentPotential: devPotential,
      vegetationType,
      heightVariation: (rand1 - 0.5) * 2,
      concreteLevel: 0,
      cureProgress: 0,
    };
  }

  getCell(x: number, y: number): Cell | null {
    if (x >= 0 && x < this.cols && y >= 0 && y < this.rows) {
      return this.cells[y][x];
    }
    return null;
  }

  setCellState(x: number, y: number, state: CellState): void {
    const cell = this.getCell(x, y);
    if (cell) {
      cell.state = state;
      this.markChanged();
    }
  }

  /**
   * Put a bush on a cell that position said was grass.
   *
   * §10's hedgerow, arriving after however many budget cycles it took to come in.
   * The only mutation of a cell's vegetation in the game: everything else about a
   * bush — that it bears fruit, that it is drawn, that it counts as standing
   * meadow — already keys off `NATURAL && 'bush'`, so this one assignment reaches
   * all of it and nothing had to learn a new word.
   *
   * Refused on ground that is not natural, and on a cell that already has a bush.
   * Returns whether anything changed, so a caller can decline to charge for it.
   *
   * What it does *not* do is create a food source. `FoodSourceManager` scans the
   * grid once, at construction, and nothing here can reach it — the same split as
   * a facility sealing cells it does not pour concrete over. `SurfaceSim` is the
   * one object that can see both.
   */
  plantBush(x: number, y: number): boolean {
    const cell = this.getCell(x, y);
    if (!cell) return false;
    if (cell.state !== CellState.NATURAL) return false;
    if (cell.vegetationType === 'bush') return false;

    cell.vegetationType = 'bush';
    this.planted.add(`${x},${y}`);
    this.markChanged();
    return true;
  }

  /** Cells with a planted bush, as `"x,y"`, ascending. Empty in most districts. */
  plantedCells(): string[] {
    return Array.from(this.planted).sort();
  }

  /** Whether a cell carries a bush somebody planted rather than one that grew. */
  isPlanted(x: number, y: number): boolean {
    return this.planted.has(`${x},${y}`);
  }

  getCols(): number {
    return this.cols;
  }

  getRows(): number {
    return this.rows;
  }

  // Count cells in each state
  countCellsByState(): Record<CellState, number> {
    const counts: Record<CellState, number> = {
      [CellState.NATURAL]: 0,
      [CellState.SURVEYED]: 0,
      [CellState.CLEARED]: 0,
      [CellState.GRADED]: 0,
      [CellState.POURED]: 0,
      [CellState.CURED]: 0,
      [CellState.PAINTED]: 0,
      [CellState.SEALED]: 0,
    };

    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        counts[this.cells[y][x].state]++;
      }
    }

    return counts;
  }

  getTotalCells(): number {
    return this.cols * this.rows;
  }

  // Get all cells for iteration
  getAllCells(): Cell[] {
    const allCells: Cell[] = [];
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        allCells.push(this.cells[y][x]);
      }
    }
    return allCells;
  }

  /**
   * Record that the ground is not what it was.
   *
   * The counter is how everything that caches against the terrain — the
   * traffic's route distances, the construction phase, the renderer's cached
   * image — finds out that its answer has expired. It is not a repaint request:
   * the grid has not known how to paint anything since ADR-008.
   */
  markChanged(): void {
    this._terrainVersion++;
  }

  /**
   * Get the terrain version counter. Incremented whenever terrain cells change.
   */
  getTerrainVersion(): number {
    return this._terrainVersion;
  }

  /**
   * Get all vegetation items (trees and bushes) with their depth for unified depth sorting.
   * Grass is rendered with the cell since it's flat on the ground.
   */
  getVegetationWithDepth(): { cell: Cell; depth: number }[] {
    const vegetation: { cell: Cell; depth: number }[] = [];
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const cell = this.cells[y][x];
        if (cell.state === CellState.NATURAL &&
            (cell.vegetationType === 'tree' || cell.vegetationType === 'bush')) {
          vegetation.push({ cell, depth: cell.y });
        }
      }
    }
    return vegetation;
  }

  /**
   * Check if a cell is part of a highway (3+ cells WIDE, not just long).
   * A highway needs width perpendicular to the travel direction.
   */
  isHighwayCell(x: number, y: number): boolean {
    const cell = this.getCell(x, y);
    if (!cell || !this.isPaved(cell.state)) return false;

    // Count horizontal span (cells in a row left-right)
    let horizontalSpan = 1;
    for (let dx = 1; dx <= 3; dx++) {
      const neighbor = this.getCell(x - dx, y);
      if (neighbor && this.isPaved(neighbor.state)) horizontalSpan++;
      else break;
    }
    for (let dx = 1; dx <= 3; dx++) {
      const neighbor = this.getCell(x + dx, y);
      if (neighbor && this.isPaved(neighbor.state)) horizontalSpan++;
      else break;
    }

    // Count vertical span (cells in a column up-down)
    let verticalSpan = 1;
    for (let dy = 1; dy <= 3; dy++) {
      const neighbor = this.getCell(x, y - dy);
      if (neighbor && this.isPaved(neighbor.state)) verticalSpan++;
      else break;
    }
    for (let dy = 1; dy <= 3; dy++) {
      const neighbor = this.getCell(x, y + dy);
      if (neighbor && this.isPaved(neighbor.state)) verticalSpan++;
      else break;
    }

    // Check for horizontal highway (3+ cells wide vertically, with road continuing left/right)
    if (verticalSpan >= 3) {
      const hasLeftNeighbor = this.getCell(x - 1, y)?.state && this.isPaved(this.getCell(x - 1, y)!.state);
      const hasRightNeighbor = this.getCell(x + 1, y)?.state && this.isPaved(this.getCell(x + 1, y)!.state);
      if (hasLeftNeighbor || hasRightNeighbor) {
        return true;
      }
    }

    // Check for vertical highway (3+ cells wide horizontally, with road continuing up/down)
    if (horizontalSpan >= 3) {
      const hasUpNeighbor = this.getCell(x, y - 1)?.state && this.isPaved(this.getCell(x, y - 1)!.state);
      const hasDownNeighbor = this.getCell(x, y + 1)?.state && this.isPaved(this.getCell(x, y + 1)!.state);
      if (hasUpNeighbor || hasDownNeighbor) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get highway lane info for a cell: orientation and perpendicular offset from center.
   * Returns null if not a highway cell.
   * offset: -1.0 to +1.0 where:
   *   For horizontal highways: -1 = top edge, +1 = bottom edge
   *   For vertical highways: -1 = left edge, +1 = right edge
   */
  getHighwayLaneInfo(x: number, y: number): { orientation: 'horizontal' | 'vertical'; offset: number } | null {
    const cell = this.getCell(x, y);
    if (!cell || !this.isPaved(cell.state)) return null;

    // Check for horizontal highway (verticalSpan >= 3, road continues left/right)
    let topEdge = y;
    for (let dy = 1; dy <= 3; dy++) {
      const n = this.getCell(x, y - dy);
      if (n && this.isPaved(n.state)) topEdge = y - dy;
      else break;
    }
    let bottomEdge = y;
    for (let dy = 1; dy <= 3; dy++) {
      const n = this.getCell(x, y + dy);
      if (n && this.isPaved(n.state)) bottomEdge = y + dy;
      else break;
    }
    const verticalSpan = bottomEdge - topEdge + 1;

    if (verticalSpan >= 3) {
      const hasLeftNeighbor = this.getCell(x - 1, y)?.state && this.isPaved(this.getCell(x - 1, y)!.state);
      const hasRightNeighbor = this.getCell(x + 1, y)?.state && this.isPaved(this.getCell(x + 1, y)!.state);
      if (hasLeftNeighbor || hasRightNeighbor) {
        const center = (topEdge + bottomEdge) / 2;
        const offset = verticalSpan <= 1 ? 0 : (y - center) / ((verticalSpan - 1) / 2);
        return { orientation: 'horizontal', offset };
      }
    }

    // Check for vertical highway (horizontalSpan >= 3, road continues up/down)
    let leftEdge = x;
    for (let dx = 1; dx <= 3; dx++) {
      const n = this.getCell(x - dx, y);
      if (n && this.isPaved(n.state)) leftEdge = x - dx;
      else break;
    }
    let rightEdge = x;
    for (let dx = 1; dx <= 3; dx++) {
      const n = this.getCell(x + dx, y);
      if (n && this.isPaved(n.state)) rightEdge = x + dx;
      else break;
    }
    const horizontalSpan = rightEdge - leftEdge + 1;

    if (horizontalSpan >= 3) {
      const hasUpNeighbor = this.getCell(x, y - 1)?.state && this.isPaved(this.getCell(x, y - 1)!.state);
      const hasDownNeighbor = this.getCell(x, y + 1)?.state && this.isPaved(this.getCell(x, y + 1)!.state);
      if (hasUpNeighbor || hasDownNeighbor) {
        const center = (leftEdge + rightEdge) / 2;
        const offset = horizontalSpan <= 1 ? 0 : (x - center) / ((horizontalSpan - 1) / 2);
        return { orientation: 'vertical', offset };
      }
    }

    return null;
  }

  /**
   * Check if a cell state is paved.
   */
  private isPaved(state: CellState): boolean {
    return state === CellState.POURED ||
           state === CellState.CURED ||
           state === CellState.PAINTED;
  }

  /**
   * Whether there is the mayor's concrete on this cell.
   *
   * The same question `isPaved` asks, in the form callers outside this class need
   * it: by coordinate rather than by state, and false off the map. A federal slab
   * is deliberately not paved — nothing drives across it (`CellState.SEALED`) —
   * which is why an undercrossing under one would be a way under nothing.
   */
  isPavedAt(x: number, y: number): boolean {
    const cell = this.getCell(x, y);
    return cell !== null && this.isPaved(cell.state);
  }

  /**
   * Count total paved cells (including highway).
   *
   * A facility's slab is not in this number. It is sealed ground and it will
   * never be natural again, but it is not the mayor's pavement and nothing
   * drives on it; `countSealedCells()` is its own count for that reason.
   */
  countPavedCells(): number {
    let count = 0;
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        if (this.isPaved(this.cells[y][x].state)) count++;
      }
    }
    return count;
  }

  /** Count cells under a federal slab. Never falls: sealed land stays sealed. */
  countSealedCells(): number {
    let count = 0;
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        if (this.cells[y][x].state === CellState.SEALED) count++;
      }
    }
    return count;
  }

  /**
   * Count total highway cells.
   */
  countHighwayCells(): number {
    let count = 0;
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        if (this.isHighwayCell(x, y)) count++;
      }
    }
    return count;
  }

  /**
   * The grid's complete state.
   *
   * Only the cells someone has touched. The rest are a pure function of
   * position (`cellNoise`), so a snapshot that listed them would be recording
   * arithmetic. `terrainVersion` rides along because the managers that cache
   * against it compare versions rather than contents: restoring the number is
   * what stops a loaded lot from looking freshly changed to a cache that has
   * already been rebuilt for it.
   *
   * The planted cells are the one exception to *"only the cells someone has
   * touched"*, and they are the reason the exception has to be spelled: a
   * hedgerow leaves a cell `NATURAL`, so it is invisible to the loop below and
   * would come back as grass.
   */
  save(): TerrainSnapshot {
    const cells: TerrainCellSnapshot[] = [];

    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const cell = this.cells[y][x];
        if (cell.state === CellState.NATURAL) continue;
        cells.push({
          x: cell.x,
          y: cell.y,
          state: cell.state,
          concreteLevel: cell.concreteLevel,
          cureProgress: cell.cureProgress,
        });
      }
    }

    const planted = this.plantedCells().map((key) => {
      const [x, y] = key.split(',').map(Number);
      return { x, y };
    });

    return { cells, planted, terrainVersion: this._terrainVersion };
  }

  /** Restore from `save()`. Every untouched cell returns to what position says it is. */
  load(state: TerrainSnapshot): void {
    // Everything back to what position says it is, then the season on top.
    this.initializeCells();

    // The hedgerows first, because they are a correction to what position said
    // and the states below are a correction to that. `plantBush` refuses a cell
    // that is not natural, and every cell is natural at this point.
    this.planted.clear();
    for (const saved of state.planted) this.plantBush(saved.x, saved.y);

    for (const saved of state.cells) {
      const cell = this.getCell(saved.x, saved.y);
      if (cell) {
        cell.state = saved.state;
        cell.concreteLevel = saved.concreteLevel;
        cell.cureProgress = saved.cureProgress;
      }
    }

    // After the bulk change, which bumps the counter.
    this.markChanged();
    this._terrainVersion = state.terrainVersion;
  }

  /**
   * Count the cells that still *carry* this vegetation.
   *
   * Natural ground only, and that qualification is the whole method. A cell's
   * `vegetationType` is a pure function of its position (`cellNoise`) and it
   * never changes: pour concrete over a bush and the cell still says `'bush'`
   * forever. So a count that asked only about `vegetationType` would report a
   * meadow that can never shrink — which is precisely what `land.bush` in the
   * Chronicle did until ADR-015, and why `seasonFlows().bushChange` was
   * documented as *"negative when the meadow shrank"* and was always zero.
   *
   * The definition used here is the one every other consumer of a bush already
   * uses: `FoodSourceManager` only puts fruit on a natural bush cell, and
   * `getVegetationWithDepth` only draws one. This is now the same question, asked
   * once.
   */
  countStandingVegetation(vegetationType: Cell['vegetationType']): number {
    let count = 0;
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const cell = this.cells[y][x];
        if (cell.state === CellState.NATURAL && cell.vegetationType === vegetationType) {
          count++;
        }
      }
    }
    return count;
  }

  /**
   * Get all natural cells (undeveloped).
   */
  getNaturalCells(): Cell[] {
    const cells: Cell[] = [];
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const cell = this.cells[y][x];
        if (cell.state === CellState.NATURAL) {
          cells.push(cell);
        }
      }
    }
    return cells;
  }
}
